import { ReactElement, useEffect } from "react"
import styled from "@emotion/styled"
import { Box, Grid } from "@mui/material"

import BulletPoints from "./components/BulletPoints/BulletPoints"
import Footer from "./components/Footer"
import DisplayImage from "./components/DisplayImage"
import IntroText from "./components/IntroText"
import Overlay from "./components/Overlay"
import PromoVideo from "./components/PromoVideo"
import Spotify from "./components/Spotify"
import InstagramGallery from "./components/InstagramGallery"
import VideoBackground from "./components/VideoBackground"
import TrustedCompanies from "./components/TrustedCompanies"
import { observeReveals } from "./lib/animations"
import { useTranslation } from "./TranslationProvider"
import Review from "./components/Review"
import { zIndex } from "./lib/theme"

window.addEventListener("load", () => document.body.classList.add("loaded"))

function App(): ReactElement {
  const { t } = useTranslation()

  useEffect(() => observeReveals(), [])

  return (
    <AppWrapper>
      <div id="home" />
      <Overlay />

      <AppBody container>
        <IntroWrapper>
          <VideoBackground
            videoSrc="video/background-video.mp4"
            posterSrc="video/background-video-backup.jpg"
          />

          <HeroContent>
            <LogoContainer>
              <Logo src="photos/logo.jpg" alt={t("media.logoAlt")} />
            </LogoContainer>

            <IntroText />
          </HeroContent>
        </IntroWrapper>

        <BodyWrapper id="body-wrapper" item sm={11} md={10} lg={9}>
          <div id="whyBookUs" className="reveal">
            <SpecialTitle>{t("app.whyBook")}</SpecialTitle>

            <BulletPoints />
          </div>

          <DisplayImage
            source="photos/group-photo1.jpg"
            alt={t("media.groupPhotoAlt")}
          />

          <div>
            <SpecialTitle $color="var(--purpleText)">
              {t("app.weddings.title")}
            </SpecialTitle>

            <SpecialTitle $fontSize={1.2} $color="var(--white)">
              {t("app.weddings.description")}
            </SpecialTitle>
          </div>

          <Review />

          <ContentWrapper id="promovideo1">
            <SpecialTitle $marginTop="3vh">{t("app.promovideo1")}</SpecialTitle>

            <PromoVideo
              videoSrc="video/promo-video-part1.mp4"
              posterSrc="video/promo-video-thumbnail.jpg"
              title={t("media.promoVideo1Title")}
            />
          </ContentWrapper>

          <ContentWrapper id="instagram">
            <SpecialTitle $marginTop="3vh">{t("app.socialMedia")}</SpecialTitle>

            <InstagramGallery />
          </ContentWrapper>

          <ContentWrapper id="repertoire">
            <SpecialTitle $marginTop="3vh">{t("app.repertoire")}</SpecialTitle>

            <Spotify />
          </ContentWrapper>

          <DisplayImage
            source="photos/group-photo2.jpg"
            alt={t("media.groupPhotoAlt")}
          />

          <Review />

          <ContentWrapper id="promovideo2">
            <SpecialTitle $marginTop="3vh">{t("app.promovideo2")}</SpecialTitle>

            <PromoVideo
              videoSrc="video/promo-video-part2.mp4"
              posterSrc="video/promo-video-thumbnail.jpg"
              title={t("media.promoVideo2Title")}
            />
          </ContentWrapper>
        </BodyWrapper>
      </AppBody>

      <div id="footer" />

      <Footer />

      <TrustedCompanies />
    </AppWrapper>
  )
}

const AppWrapper = styled(Box)`
  width: 100vw;

  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  .reveal {
    position: relative;
    transform: translateY(50px);
    opacity: 0;
    transition:
      opacity 0.8s ease,
      transform 0.8s ease;
  }

  .reveal.active {
    transform: translateY(0);
    opacity: 1;
  }
`

const AppBody = styled(Grid)`
  margin-top: calc(65px - min(220px, 18vw));

  padding: 0 1rem;
  max-width: 120rem;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: min(2rem, 5vh);

  @media only screen and (min-width: 600px) {
    padding: 0 2rem;
  }
`

const IntroWrapper = styled(Grid)`
  position: relative;

  min-height: min(100vh, 1000px);
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

const HeroContent = styled.div`
  position: relative;
  z-index: 1;

  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  * {
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  }
`

const LogoContainer = styled.div`
  position: relative;
  z-index: ${zIndex.logo};
  aspect-ratio: 1;

  width: 80vw;
  max-width: 64rem;

  border-radius: 50%;
  overflow: hidden;

  border: 4px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
`

const Logo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`

const BodyWrapper = styled(Grid)`
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
`

const ContentWrapper = styled(Grid)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;

  user-select: none;
`

export const SpecialTitle = styled.h1<{
  $marginTop?: string
  $color?: string
  $fontSize?: number
}>`
  margin: 0;
  margin-top: min(${(props) => props.$marginTop || "0"}, 50px);

  color: ${(props) => props.$color || "var(--special)"};
  font-weight: 900;
  font-size: ${(props) => props.$fontSize || 2}rem;
  text-shadow: 0 0 3rem rgba(0, 0, 0, 0.5);

  user-select: none;

  @media screen and (max-width: 500px) {
    font-size: ${(props) => (props.$fontSize || 2) * 0.75}rem;
  }
`

export default App
